import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiBriefcase,
    FiCheckCircle,
    FiClock,
    FiEdit2,
    FiEye,
    FiFilter,
    FiFlag,
    FiMessageSquare,
    FiPlus,
    FiRefreshCw,
    FiSearch,
    FiTrash2,
    FiUser,
    FiXCircle,
    FiActivity,
} from "react-icons/fi";

import API from "../../../../../utils/apiCalling";
import { config } from "../../../../../utils/apiUrl";
import useSwal from "../../../../../hooks/useSwal";

import AddEditTask from "./AddEditTask";
import TaskDetailsDrawer from "./TaskDetailsDrawer";
import AddTaskComment from "./AddTaskComment";

import "./TaskManagement.css";
import ChangeTaskStatus from "./ChangeTaskStatus";

const DEFAULT_FILTERS = {
    search: "",
    assignedTo: "",
    status: "",
    priority: "",
    category: "",
    sourceType: "",
    overdueOnly: false,
    dueTodayOnly: false,
    followUpDueOnly: false,
    unreadOnly: false,
    sortBy: "createdAt",
    sortOrder: "desc",
};

const STATUS_OPTIONS = [
    {
        value: "",
        label: "All Statuses",
    },
    {
        value: "PENDING",
        label: "Pending",
    },
    {
        value: "IN_PROGRESS",
        label: "In Progress",
    },
    {
        value: "WAITING_FOLLOW_UP",
        label: "Waiting Follow-up",
    },
    {
        value: "ON_HOLD",
        label: "On Hold",
    },
    {
        value: "COMPLETED",
        label: "Completed",
    },
    {
        value: "CLOSED",
        label: "Closed",
    },
    {
        value: "CANCELLED",
        label: "Cancelled",
    },
];

const PRIORITY_OPTIONS = [
    {
        value: "",
        label: "All Priorities",
    },
    {
        value: "LOW",
        label: "Low",
    },
    {
        value: "MEDIUM",
        label: "Medium",
    },
    {
        value: "HIGH",
        label: "High",
    },
    {
        value: "URGENT",
        label: "Urgent",
    },
];

const formatLabel = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map(
            (word) =>
                word.charAt(0).toUpperCase() +
                word.slice(1)
        )
        .join(" ");

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                dateStyle: "medium",
                timeStyle: "short",
            }
            : {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
    ).format(date);
};

const getTaskRowClass = (task) => {
    if (task?.hasAdminUnreadActivity) {
        return "task-management-row task-management-row-unread";
    }

    if (task?.isOverdue) {
        return "task-management-row task-management-row-overdue";
    }

    if (task?.priority === "URGENT") {
        return "task-management-row task-management-row-urgent";
    }

    if (task?.isFollowUpDue) {
        return "task-management-row task-management-row-follow-up";
    }

    return "task-management-row";
};

const TaskManagement = () => {
    const api = useMemo(
        () => new API(),
        []
    );

    const swal = useSwal();

    const [tasks, setTasks] =
        useState([]);

    const [summary, setSummary] =
        useState({
            total: 0,
            open: 0,
            overdue: 0,
            dueToday: 0,
            followUpDue: 0,
            unread: 0,
            status: {
                pending: 0,
                inProgress: 0,
                waitingFollowUp: 0,
                onHold: 0,
                completed: 0,
                closed: 0,
                cancelled: 0,
            },
        });

    const [pagination, setPagination] =
        useState({
            page: 1,
            limit: 10,
            totalRecords: 0,
            totalPages: 1,
        });

    const [filters, setFilters] =
        useState({
            ...DEFAULT_FILTERS,
        });

    const [employees, setEmployees] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [summaryLoading, setSummaryLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    const [taskFormOpen, setTaskFormOpen] =
        useState(false);

    const [selectedTask, setSelectedTask] =
        useState(null);

    const [taskDetailsOpen, setTaskDetailsOpen] =
        useState(false);

    const [taskCommentOpen, setTaskCommentOpen] =
        useState(false);
    const [
        taskStatusOpen,
        setTaskStatusOpen,
    ] = useState(false);
    const fetchEmployees =
        useCallback(async () => {
            try {
                const response = await api.get(
                    `${config.getTaskEmployeeDropdown}?limit=200`
                );

                setEmployees(
                    response?.status === "success" &&
                        Array.isArray(response?.data)
                        ? response.data
                        : []
                );
            } catch (requestError) {
                console.error(
                    "Task employee dropdown error:",
                    requestError
                );

                setEmployees([]);
            }
        }, [api]);

    const fetchSummary =
        useCallback(async () => {
            try {
                setSummaryLoading(true);

                const query =
                    new URLSearchParams();

                if (filters.assignedTo) {
                    query.set(
                        "assignedTo",
                        filters.assignedTo
                    );
                }

                if (filters.sourceType) {
                    query.set(
                        "sourceType",
                        filters.sourceType
                    );
                }

                const response = await api.get(
                    `${config.getTaskSummary}?${query.toString()}`
                );

                if (response?.status === "success") {
                    setSummary((previous) => ({
                        ...previous,
                        ...response.data,
                        status: {
                            ...previous.status,
                            ...(response?.data?.status || {}),
                        },
                    }));
                }
            } catch (requestError) {
                console.error(
                    "Task summary error:",
                    requestError
                );
            } finally {
                setSummaryLoading(false);
            }
        }, [
            api,
            filters.assignedTo,
            filters.sourceType,
        ]);

    const fetchTasks =
        useCallback(async () => {
            try {
                setLoading(true);
                setError("");

                const payload = {
                    page: pagination.page,
                    limit: pagination.limit,
                    ...filters,
                };

                const response = await api.post(
                    config.getTaskList,
                    payload
                );

                if (response?.status !== "success") {
                    throw new Error(
                        response?.message ||
                        "Unable to fetch tasks."
                    );
                }

                setTasks(
                    Array.isArray(response?.data)
                        ? response.data
                        : []
                );

                setPagination((previous) => ({
                    ...previous,
                    page: Number(
                        response?.pagination?.page ||
                        previous.page
                    ),
                    limit: Number(
                        response?.pagination?.limit ||
                        previous.limit
                    ),
                    totalRecords: Number(
                        response?.pagination
                            ?.totalRecords ||
                        0
                    ),
                    totalPages: Math.max(
                        Number(
                            response?.pagination
                                ?.totalPages || 1
                        ),
                        1
                    ),
                }));
            } catch (requestError) {
                console.error(
                    "Get task list error:",
                    requestError
                );

                setTasks([]);

                setError(
                    requestError?.response?.data
                        ?.message ||
                    requestError?.message ||
                    "Unable to fetch tasks."
                );
            } finally {
                setLoading(false);
            }
        }, [
            api,
            pagination.page,
            pagination.limit,
            filters,
        ]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const refreshPage = async () => {
        await Promise.all([
            fetchTasks(),
            fetchSummary(),
        ]);
    };

    const handleFilterChange = (
        event
    ) => {
        const {
            name,
            value,
            type,
            checked,
        } = event.target;

        setFilters((previous) => ({
            ...previous,
            [name]:
                type === "checkbox"
                    ? checked
                    : value,
        }));

        setPagination((previous) => ({
            ...previous,
            page: 1,
        }));
    };

    const clearFilters = () => {
        setFilters({
            ...DEFAULT_FILTERS,
        });

        setPagination((previous) => ({
            ...previous,
            page: 1,
        }));
    };

    const handleAddTask = () => {
        setSelectedTask(null);
        setTaskFormOpen(true);
    };

    const handleEditTask = (task) => {
        setSelectedTask(task);
        setTaskDetailsOpen(false);
        setTaskFormOpen(true);
    };

    const handleViewTask = (task) => {
        setSelectedTask(task);
        setTaskDetailsOpen(true);
    };

    const handleAddNote = (task) => {
        setSelectedTask(task);
        setTaskDetailsOpen(false);
        setTaskCommentOpen(true);
    };

    const handleTaskSaved = async () => {
        setTaskFormOpen(false);
        setSelectedTask(null);

        await refreshPage();
    };

    const handleCommentSaved = async () => {
        setTaskCommentOpen(false);
        setTaskDetailsOpen(true);

        await refreshPage();
    };

    const handleDeleteTask = async (
        task
    ) => {
        const result =
            await swal.confirm({
                icon: "warning",
                title: "Delete Task?",
                text:
                    `${task.taskCode}: ${task.title}`,
                confirmButtonText:
                    "Delete Task",
                cancelButtonText: "Cancel",
            });

        if (!result?.isConfirmed) {
            return;
        }

        try {
            const response =
                await api.delete(
                    config.deleteTask(task._id)
                );

            if (
                response?.status !== "success"
            ) {
                throw new Error(
                    response?.message ||
                    "Unable to delete task."
                );
            }

            await swal.success({
                icon: "success",
                title: "Task Deleted",
                text:
                    response?.message ||
                    "Task deleted successfully.",
                timer: 1400,
                showConfirmButton: false,
            });

            await refreshPage();
        } catch (requestError) {
            swal.error({
                icon: "error",
                title:
                    "Unable to delete task",
                text:
                    requestError?.response?.data
                        ?.message ||
                    requestError?.message ||
                    "Unable to delete task.",
            });
        }
    };

    const summaryCards = [
        {
            key: "total",
            label: "Total Tasks",
            value: summary.total,
            icon: FiBriefcase,
            className: "total",
        },
        {
            key: "pending",
            label: "Pending",
            value:
                summary.status?.pending || 0,
            icon: FiClock,
            className: "pending",
        },
        {
            key: "inProgress",
            label: "In Progress",
            value:
                summary.status?.inProgress ||
                0,
            icon: FiRefreshCw,
            className: "progress",
        },
        {
            key: "followUp",
            label: "Waiting Follow-up",
            value:
                summary.status
                    ?.waitingFollowUp || 0,
            icon: FiMessageSquare,
            className: "follow-up",
        },
        {
            key: "overdue",
            label: "Overdue",
            value: summary.overdue || 0,
            icon: FiAlertCircle,
            className: "overdue",
        },
        {
            key: "completed",
            label: "Completed",
            value:
                summary.status?.completed ||
                0,
            icon: FiCheckCircle,
            className: "completed",
        },
        {
            key: "closed",
            label: "Closed",
            value:
                summary.status?.closed || 0,
            icon: FiXCircle,
            className: "closed",
        },
        {
            key: "unread",
            label: "New Updates",
            value: summary.unread || 0,
            icon: FiMessageSquare,
            className: "unread",
        },
    ];

    const handleChangeTaskStatus = (
        task
    ) => {
        setSelectedTask(task);

        setTaskDetailsOpen(false);
        setTaskFormOpen(false);
        setTaskCommentOpen(false);

        setTaskStatusOpen(true);
    };

    const handleCloseTaskStatus =
        () => {
            setTaskStatusOpen(false);
            setSelectedTask(null);
        };

    const handleTaskStatusSuccess =
        async (updatedTask) => {
            setTaskStatusOpen(false);

            setSelectedTask(
                updatedTask || null
            );

            await refreshPage();
        };

    return (
            <div className="task-management-page">
                <header className="task-management-header">
                    <div>
                        <span className="task-management-header-icon">
                            <FiBriefcase />
                        </span>

                        <div>
                            <small>
                                COMKWIK ADMIN
                            </small>

                            <h1>
                                Task Management
                            </h1>

                            <p>
                                Create, assign, monitor and follow up employee tasks.
                            </p>
                        </div>
                    </div>

                    <div className="task-management-header-actions">
                        <button
                            type="button"
                            className="task-management-refresh-button"
                            onClick={refreshPage}
                            disabled={loading}
                        >
                            <FiRefreshCw />
                            Refresh
                        </button>

                        <button
                            type="button"
                            className="task-management-add-button"
                            onClick={handleAddTask}
                        >
                            <FiPlus />
                            Create Task
                        </button>
                    </div>
                </header>

                <section className="task-management-summary-grid">
                    {summaryCards.map((card) => {
                        const Icon = card.icon;

                        return (
                            <article
                                key={card.key}
                                className={`task-management-summary-card ${card.className}`}
                            >
                                <div>
                                    <span>
                                        {card.label}
                                    </span>

                                    <strong>
                                        {summaryLoading
                                            ? "..."
                                            : card.value}
                                    </strong>
                                </div>

                                <span className="task-management-summary-icon">
                                    <Icon />
                                </span>
                            </article>
                        );
                    })}
                </section>

                <section className="task-management-filters">
                    <div className="task-management-filter-heading">
                        <div>
                            <FiFilter />

                            <div>
                                <h2>
                                    Search & Filters
                                </h2>

                                <p>
                                    Find tasks by employee, status, priority or follow-up.
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={clearFilters}
                        >
                            Clear Filters
                        </button>
                    </div>

                    <div className="task-management-filter-grid">
                        <div className="task-management-search">
                            <FiSearch />

                            <input
                                type="text"
                                name="search"
                                value={filters.search}
                                onChange={
                                    handleFilterChange
                                }
                                placeholder="Search task code, title, customer, mobile..."
                            />
                        </div>

                        <select
                            name="assignedTo"
                            value={
                                filters.assignedTo
                            }
                            onChange={
                                handleFilterChange
                            }
                        >
                            <option value="">
                                All Employees
                            </option>

                            {employees.map(
                                (employee) => (
                                    <option
                                        key={employee._id}
                                        value={employee._id}
                                    >
                                        {employee.name}
                                        {employee.employeeCode
                                            ? ` • ${employee.employeeCode}`
                                            : ""}
                                    </option>
                                )
                            )}
                        </select>

                        <select
                            name="status"
                            value={filters.status}
                            onChange={
                                handleFilterChange
                            }
                        >
                            {STATUS_OPTIONS.map(
                                (option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                )
                            )}
                        </select>

                        <select
                            name="priority"
                            value={filters.priority}
                            onChange={
                                handleFilterChange
                            }
                        >
                            {PRIORITY_OPTIONS.map(
                                (option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                )
                            )}
                        </select>

                        <select
                            name="sourceType"
                            value={
                                filters.sourceType
                            }
                            onChange={
                                handleFilterChange
                            }
                        >
                            <option value="">
                                All Sources
                            </option>

                            <option value="GENERAL">
                                Manual Task
                            </option>

                            <option value="CUSTOMER_REQUIREMENT">
                                Customer Requirement
                            </option>

                            <option value="ORDER">
                                Order
                            </option>

                            <option value="REPAIR_JOB">
                                Repair Job
                            </option>
                        </select>

                        <label className="task-management-check-filter">
                            <input
                                type="checkbox"
                                name="overdueOnly"
                                checked={
                                    filters.overdueOnly
                                }
                                onChange={
                                    handleFilterChange
                                }
                            />

                            <span>
                                Overdue only
                            </span>
                        </label>

                        <label className="task-management-check-filter">
                            <input
                                type="checkbox"
                                name="followUpDueOnly"
                                checked={
                                    filters.followUpDueOnly
                                }
                                onChange={
                                    handleFilterChange
                                }
                            />

                            <span>
                                Follow-up due
                            </span>
                        </label>

                        <label className="task-management-check-filter">
                            <input
                                type="checkbox"
                                name="unreadOnly"
                                checked={
                                    filters.unreadOnly
                                }
                                onChange={
                                    handleFilterChange
                                }
                            />

                            <span>
                                New updates
                            </span>
                        </label>
                    </div>
                </section>

                <section className="task-management-table-card">
                    <div className="task-management-table-heading">
                        <div>
                            <h2>
                                All Tasks
                            </h2>

                            <span>
                                {pagination.totalRecords} records
                            </span>
                        </div>
                    </div>

                    {error ? (
                        <div className="task-management-error">
                            <FiAlertCircle />

                            <span>{error}</span>

                            <button
                                type="button"
                                onClick={refreshPage}
                            >
                                Try Again
                            </button>
                        </div>
                    ) : null}

                    <div className="task-management-table-wrapper">
                        <table className="task-management-table">
                            <thead>
                                <tr>
                                    <th>Task</th>
                                    <th>Source</th>
                                    <th>Assigned Employee</th>
                                    <th>Priority</th>
                                    <th>Status</th>
                                    <th>Due Date</th>
                                    <th>Follow-up</th>
                                    <th>Progress</th>
                                    <th>Latest Activity</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={10}>
                                            <div className="task-management-loading">
                                                Loading tasks...
                                            </div>
                                        </td>
                                    </tr>
                                ) : tasks.length === 0 ? (
                                    <tr>
                                        <td colSpan={10}>
                                            <div className="task-management-empty">
                                                <FiBriefcase />

                                                <h3>
                                                    No tasks found
                                                </h3>

                                                <p>
                                                    Create a manual task or assign one from a customer requirement.
                                                </p>

                                                <button
                                                    type="button"
                                                    onClick={
                                                        handleAddTask
                                                    }
                                                >
                                                    <FiPlus />
                                                    Create First Task
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    tasks.map((task) => (
                                        <tr
                                            key={task._id}
                                            className={getTaskRowClass(
                                                task
                                            )}
                                        >
                                            <td>
                                                <div className="task-management-task-cell">
                                                    <span>
                                                        {task.taskCode}
                                                    </span>

                                                    <strong>
                                                        {task.title}
                                                    </strong>

                                                    {task.hasAdminUnreadActivity ? (
                                                        <small className="task-management-new-update">
                                                            New Update
                                                        </small>
                                                    ) : null}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="task-management-source-cell">
                                                    <strong>
                                                        {task?.source
                                                            ?.sourceType ===
                                                            "GENERAL"
                                                            ? "Manual Task"
                                                            : formatLabel(
                                                                task?.source
                                                                    ?.sourceType
                                                            )}
                                                    </strong>

                                                    <span>
                                                        {task?.source
                                                            ?.sourceCode ||
                                                            task
                                                                ?.customerContext
                                                                ?.customerName ||
                                                            "-"}
                                                    </span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="task-management-employee-cell">
                                                    <span>
                                                        {String(
                                                            task?.assignedTo
                                                                ?.name || "E"
                                                        )
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                    </span>

                                                    <div>
                                                        <strong>
                                                            {task?.assignedTo
                                                                ?.name ||
                                                                "-"}
                                                        </strong>

                                                        <small>
                                                            {[
                                                                task
                                                                    ?.assignedTo
                                                                    ?.employeeCode,
                                                                task
                                                                    ?.assignedTo
                                                                    ?.department,
                                                            ]
                                                                .filter(
                                                                    Boolean
                                                                )
                                                                .join(" • ")}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <span
                                                    className={`task-management-priority priority-${String(
                                                        task.priority ||
                                                        ""
                                                    ).toLowerCase()}`}
                                                >
                                                    <FiFlag />
                                                    {formatLabel(
                                                        task.priority
                                                    )}
                                                </span>
                                            </td>

                                            <td>
                                                <span
                                                    className={`task-management-status status-${String(
                                                        task.status ||
                                                        ""
                                                    )
                                                        .toLowerCase()
                                                        .replace(
                                                            /_/g,
                                                            "-"
                                                        )}`}
                                                >
                                                    {formatLabel(
                                                        task.status
                                                    )}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="task-management-date-cell">
                                                    <strong>
                                                        {formatDate(
                                                            task.dueDate
                                                        )}
                                                    </strong>

                                                    {task.isOverdue ? (
                                                        <small className="danger">
                                                            Overdue
                                                        </small>
                                                    ) : task.isDueToday ? (
                                                        <small className="warning">
                                                            Due today
                                                        </small>
                                                    ) : null}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="task-management-date-cell">
                                                    <strong>
                                                        {formatDate(
                                                            task.followUpAt,
                                                            true
                                                        )}
                                                    </strong>

                                                    {task.isFollowUpDue ? (
                                                        <small className="warning">
                                                            Follow-up due
                                                        </small>
                                                    ) : null}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="task-management-progress-cell">
                                                    <span>
                                                        {Number(
                                                            task.progress ||
                                                            0
                                                        )}
                                                        %
                                                    </span>

                                                    <div>
                                                        <strong
                                                            style={{
                                                                width: `${Math.min(
                                                                    Math.max(
                                                                        Number(
                                                                            task.progress ||
                                                                            0
                                                                        ),
                                                                        0
                                                                    ),
                                                                    100
                                                                )}%`,
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="task-management-activity-cell">
                                                    <strong>
                                                        {task.latestActivityMessage ||
                                                            "-"}
                                                    </strong>

                                                    <span>
                                                        {formatDate(
                                                            task.latestActivityAt,
                                                            true
                                                        )}
                                                    </span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="task-management-actions">
                                                    <button
                                                        type="button"
                                                        className="view"
                                                        title="View task"
                                                        onClick={() =>
                                                            handleViewTask(task)
                                                        }
                                                    >
                                                        <FiEye />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="note"
                                                        title="Add note"
                                                        onClick={() =>
                                                            handleAddNote(task)
                                                        }
                                                    >
                                                        <FiMessageSquare />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="edit"
                                                        title="Edit task"
                                                        onClick={() =>
                                                            handleEditTask(task)
                                                        }
                                                    >
                                                        <FiEdit2 />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="status"
                                                        title="Change task status"
                                                        onClick={() =>
                                                            handleChangeTaskStatus(
                                                                task
                                                            )
                                                        }
                                                    >
                                                        <FiActivity />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="delete"
                                                        title="Delete task"
                                                        onClick={() =>
                                                            handleDeleteTask(task)
                                                        }
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <footer className="task-management-pagination">
                        <div>
                            Showing{" "}
                            <strong>
                                {tasks.length}
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {pagination.totalRecords}
                            </strong>{" "}
                            tasks
                        </div>

                        <div>
                            <select
                                value={pagination.limit}
                                onChange={(event) =>
                                    setPagination(
                                        (previous) => ({
                                            ...previous,
                                            page: 1,
                                            limit: Number(
                                                event.target.value
                                            ),
                                        })
                                    )
                                }
                            >
                                <option value={10}>
                                    10 / page
                                </option>

                                <option value={20}>
                                    20 / page
                                </option>

                                <option value={50}>
                                    50 / page
                                </option>
                            </select>

                            <button
                                type="button"
                                disabled={
                                    pagination.page <= 1
                                }
                                onClick={() =>
                                    setPagination(
                                        (previous) => ({
                                            ...previous,
                                            page:
                                                previous.page - 1,
                                        })
                                    )
                                }
                            >
                                Previous
                            </button>

                            <span>
                                Page{" "}
                                <strong>
                                    {pagination.page}
                                </strong>{" "}
                                of{" "}
                                <strong>
                                    {pagination.totalPages}
                                </strong>
                            </span>

                            <button
                                type="button"
                                disabled={
                                    pagination.page >=
                                    pagination.totalPages
                                }
                                onClick={() =>
                                    setPagination(
                                        (previous) => ({
                                            ...previous,
                                            page:
                                                previous.page + 1,
                                        })
                                    )
                                }
                            >
                                Next
                            </button>
                        </div>
                    </footer>
                </section>

                <AddEditTask
                    open={taskFormOpen}
                    task={selectedTask}
                    sourceContext={null}
                    onClose={() => {
                        setTaskFormOpen(false);
                        setSelectedTask(null);
                    }}
                    onSuccess={
                        handleTaskSaved
                    }
                />

                <TaskDetailsDrawer
                    open={taskDetailsOpen}
                    task={selectedTask}
                    taskId={selectedTask?._id}
                    onClose={() => {
                        setTaskDetailsOpen(false);
                        setSelectedTask(null);
                    }}
                    onEdit={handleEditTask}
                    onAddNote={handleAddNote}
                    onRefresh={refreshPage}
                    onChangeStatus={handleChangeTaskStatus}
                />

                <AddTaskComment
                    open={taskCommentOpen}
                    task={selectedTask}
                    onClose={() => {
                        setTaskCommentOpen(false);
                        setSelectedTask(null);
                    }}
                    onSuccess={
                        handleCommentSaved
                    }
                />
                <ChangeTaskStatus
                    open={taskStatusOpen}
                    task={selectedTask}
                    onClose={
                        handleCloseTaskStatus
                    }
                    onSuccess={
                        handleTaskStatusSuccess
                    }
                />
            </div>
    );
};

export default TaskManagement;